import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Cart } from '../types/cart';
import type { CatalogItem } from '../types/catalogItem';
import type { Preview } from '../types/preview';
import type { ProductCmsData } from '../types/productCms';

/** The purchasable catalog plus flags describing what it contains. */
export interface Catalog {
  items: CatalogItem[];
  hasEduProduct: boolean;
  hasCloudCredits: boolean;
}

export interface CartState {
  loading: boolean;
  initialized: boolean;
  catalog: Catalog | null;
  productsPreview: Preview | null;
  productsCmsData: ProductCmsData[] | null;
  persistentCart: Cart | null;
  promoCode: string;
}

const initialState: CartState = {
  loading: false,
  initialized: false,
  catalog: null,
  productsPreview: null,
  productsCmsData: null,
  persistentCart: null,
  promoCode: '',
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setInitialized(state, action: PayloadAction<boolean>) {
      state.initialized = action.payload;
    },
    setCartProductQuantity(
      state,
      action: PayloadAction<{ ratePlan: string; quantity: number }>,
    ) {
      const product = state.persistentCart?.products.find(
        (p) => p.ratePlan === action.payload.ratePlan,
      );
      if (product) {
        product.quantity = Math.max(1, Math.floor(action.payload.quantity));
      }
    },
    setPromoCode(state, action: PayloadAction<string>) {
      state.promoCode = action.payload;
    },
    setPersistentCart(state, action: PayloadAction<Cart | null>) {
      state.persistentCart = action.payload;
    },
    setCatalog(state, action: PayloadAction<Catalog | null>) {
      state.catalog = action.payload;
    },
    setProductsPreview(state, action) {
      state.productsPreview = action.payload;
    },
    setProductsCmsData(state, action) {
      state.productsCmsData = action.payload;
    },
  },
});

export const {
  setLoading,
  setInitialized,
  setCatalog,
  setProductsPreview,
  setProductsCmsData,
  setCartProductQuantity,
  setPromoCode,
  setPersistentCart,
} = cartSlice.actions;

export default cartSlice.reducer;
